import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const SITE_KEY =
  process.env.REACT_APP_HCAPTCHA_SITEKEY || '54443e30-488a-495a-abf4-acce12fab18c';

/**
 * HCaptchaField — shared hCaptcha handling (06 §6.2).
 * - sitekey from env with public fallback
 * - token is one-shot: exposed via `onToken`, cleared on expire/error
 * - exposes `reset()` to invalidate the token after a submit attempt
 */
const HCaptchaField = forwardRef(function HCaptchaField({ onToken, onError, theme = 'dark' }, ref) {
  const { t } = useTranslation('cy');
  const captchaRef = useRef(null);
  const [expired, setExpired] = useState(false);

  const clear = () => {
    setExpired(false);
    onToken && onToken(null);
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      captchaRef.current?.resetCaptcha();
      clear();
    },
  }));

  return (
    <div>
      <div className="cy-hcaptcha">
        <HCaptcha
          sitekey={SITE_KEY}
          theme={theme}
          ref={captchaRef}
          onVerify={(token) => {
            setExpired(false);
            onToken && onToken(token);
          }}
          onExpire={() => {
            setExpired(true);
            onToken && onToken(null);
          }}
          onChalExpired={() => {
            setExpired(true);
            onToken && onToken(null);
          }}
          onError={() => {
            onError && onError();
            onToken && onToken(null);
          }}
        />
      </div>
      {expired ? (
        <p className="cy-fineprint" style={{ color: 'var(--cy-color-warning)' }}>
          {t('ui.skipExpired')}
        </p>
      ) : null}
      <p className="cy-fineprint">{t('ui.captchaNote')}</p>
    </div>
  );
});

export default HCaptchaField;
